import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PhotoTile from '../components/PhotoTile';
import OfflinePill from '../components/OfflinePill';
import { SkeletonGrid } from '../components/Skeleton';
import { IconPlay } from '../components/Icon';
import { mediaLoaded, useMediaPolling, useMedia, visibleTo } from '../lib/media';
import { useSession } from '../lib/session';
import { useSchedule } from '../lib/content';
import { dayKey, now } from '../lib/time';

type Filter = 'all' | 'mine' | 'hidden';

const PAGE = 24;

/**
 * Artboard 1h — the social surface of the whole site.
 * Every guest sees every upload (D35). One filter, not separate pages; square
 * tiles so six hundred of them scan quickly; the uploader's name on a card foot
 * under the image, with their team's suit mark beside it.
 */
export default function Gallery() {
  const session = useSession();
  const media = useMedia();
  // Other guests' uploads arrive by poll, not by push (D12/D17).
  useMediaPolling(20_000);
  const schedule = useSchedule();
  const [filter, setFilter] = useState<Filter>('all');
  const [count, setCount] = useState(PAGE);
  const sentinel = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => visibleTo(media, session), [media, session]);

  const filtered = useMemo(() => {
    if (filter === 'mine') return visible.filter((it) => it.uploaderId === session?.userId);
    if (filter === 'hidden') return visible.filter((it) => it.hidden);
    return visible.filter((it) => !it.hidden);
  }, [visible, filter, session]);

  // Cursor pagination, standing in for the API's. There will be thousands.
  useEffect(() => { setCount(PAGE); }, [filter]);
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setCount((n) => n + PAGE);
    }, { rootMargin: '600px' });
    io.observe(node);
    return () => io.disconnect();
  }, [filtered.length]);

  const page = filtered.slice(0, count);
  const eventName = (id?: string) => schedule.data?.events.find((e) => e.id === id)?.name;

  // A quiet grouping: today's uploads sit under the event they came from,
  // everything older just says its day.
  const heading = (() => {
    const first = page[0];
    if (!first) return null;
    if (dayKey(first.createdAt) === dayKey(now())) return eventName(first.eventId) ?? 'Today';
    return eventName(first.eventId) ?? 'Earlier this week';
  })();

  // "No photos yet" and "we have not asked yet" look identical from the item
  // count alone, and showing the empty state to someone whose gallery is still
  // loading tells them their photos are gone.
  if (!mediaLoaded() && media.length === 0) return <div className="page"><SkeletonGrid /></div>;

  return (
    <div className="page">
      <OfflinePill />

      <div className={`gal-bar${session?.isAdmin ? ' is-admin' : ''}`}>
        <div className="seg" style={{ fontSize: 12 }}>
          <FilterOption value="all" filter={filter} setFilter={setFilter} label="All" />
          <FilterOption value="mine" filter={filter} setFilter={setFilter} label="Mine" />
          {session?.isAdmin && <FilterOption value="hidden" filter={filter} setFilter={setFilter} label="Hidden" />}
        </div>
        <div style={{ flex: 1 }} />
        {session?.isAdmin && (
          /* The slideshow lives with the photos, not in Profile — it is a thing
             you do to this collection, and the person starting it on the
             reception laptop is already looking at the gallery. */
          <Link className="btn btn-secondary" style={{ flex: 'none', gap: 6, minHeight: 36 }} to="/display">
            <IconPlay size={13} /> Slideshow
          </Link>
        )}
        <span className="gal-count">{visible.filter((it) => !it.hidden).length} photos so far</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyFor filter={filter} />
      ) : (
        <>
          {heading && (
            <div className="gal-head">
              <h4>{heading}</h4>
              <div className="gal-count">
                {filter === 'mine' ? `${filtered.length} of yours` : `${filtered.length} to look through`}
              </div>
            </div>
          )}
          <div className="gal-grid">
            {page.map((item) => <PhotoTile key={item.id} item={item} showState={filter === 'mine'} />)}
          </div>
          <div ref={sentinel} style={{ height: 1 }} />
          {count < filtered.length && <SkeletonGrid tiles={2} />}
        </>
      )}
    </div>
  );
}

function FilterOption({ value, filter, setFilter, label }: {
  value: Filter; filter: Filter; setFilter: (f: Filter) => void; label: string;
}) {
  return (
    <label className="seg-opt">
      <input type="radio" name="gallery-filter" checked={filter === value} onChange={() => setFilter(value)} />
      {label}
    </label>
  );
}

function EmptyFor({ filter }: { filter: Filter }) {
  if (filter === 'hidden') {
    return (
      <div className="empty">
        <div className="mark" aria-hidden="true">♣</div>
        <h4 style={{ margin: 0 }}>Nothing is hidden</h4>
        <p>Anything you hide from a photo's own screen collects here, and can be put back from the same place.</p>
      </div>
    );
  }
  if (filter === 'mine') {
    // Invite rather than apologise. The All view will not be empty for long.
    return (
      <div className="empty">
        <div className="mark" aria-hidden="true">♥</div>
        <h4 style={{ margin: 0 }}>Nothing of yours yet</h4>
        <p>Whatever you take this week ends up here, with your name on it — and on the big screen at the reception.</p>
        <Link className="btn btn-primary" to="/upload">Add the first one</Link>
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="mark" aria-hidden="true">♦</div>
      <h4 style={{ margin: 0 }}>No photos yet</h4>
      <p>Somebody has to go first. It may as well be you.</p>
      <Link className="btn btn-primary" to="/upload">Add a photo</Link>
    </div>
  );
}
